/**
 * community: how a chart point of each widget maps back to the rows behind it,
 * for the conversations drill-down. Same filtering as the widget's loader.
 *
 * Category refs carry the category's key (`category.value`): a community, a
 * voice, a division, an age band or a topic. The activity grid's cell is
 * `category: { dimension: 'weekday', value: '0'…'6' }` (Sunday first) plus
 * `series: { dimension: 'hour', value: '0'…'23' }`.
 */
import { bucketStart } from '@/lib/analytics/buckets';
import { isoToMs } from '@/lib/analytics/dates';

import { byVoice, complaints, inCommunity } from '../engine/cuts';
import { COMMUNITY_POST_CONVERSATIONS, type MentionRow } from '../engine/generator';
import { scoped } from '../query';
import { activityRows } from '../widgets/community';
import type { DrillResolver, DrillResolvers } from './types';

/** Rows of a cut whose `field` equals the ref's category, or the whole cut without one. */
function byCategory(
  cut: (query: Parameters<DrillResolver>[1]) => readonly MentionRow[],
  read: (row: MentionRow) => string | null,
): DrillResolver {
  return (ref, query) => {
    const rows = cut(query);
    const value = ref.category?.value;
    return value === undefined ? rows : rows.filter((row) => read(row) === value);
  };
}

const communityRows = (query: Parameters<DrillResolver>[1]) => inCommunity(scoped(query).own);

export const communityDrillResolvers: DrillResolvers = {
  'activity-by-day-and-hour': (ref, query) => {
    const rows = activityRows(query);
    const weekday = ref.category ? Number(ref.category.value) : undefined;
    const hour = ref.series ? Number(ref.series.value) : undefined;
    return rows.filter(
      (row) =>
        (weekday === undefined || new Date(row.day).getDay() === weekday) &&
        (hour === undefined || row.hour === hour),
    );
  },

  'top-communities-by-mention': byCategory(communityRows, (row) => row.community),
  'top-communities-by-sentiment': byCategory(communityRows, (row) => row.community),
  'community-topics': byCategory(communityRows, (row) => row.topic),

  'community-activity-timeline': (ref, query) => {
    const rows = communityRows(query);
    const bucket = ref.bucket;
    if (!bucket) return rows;
    const start = isoToMs(bucket.start);
    const week = rows.filter((row) => bucketStart(row.day, bucket.unit) === start);
    /* Both series, posts first — what the two lines add up to (the prototype's order). */
    const isPost = (row: MentionRow) => COMMUNITY_POST_CONVERSATIONS.includes(row.conversation);
    return [...week.filter(isPost), ...week.filter((row) => !isPost(row))];
  },

  /* The pyramid plots negative mentions only, so its bars open negatives only. */
  'audience-age-and-gender': byCategory(
    (query) => scoped(query).own.filter((row) => row.sentiment === 'negative'),
    (row) => row.ageBand,
  ),

  'top-individual-voices': byCategory((query) => byVoice(scoped(query).own), (row) => row.voice),

  /* A bar is a division's complaints — not every mention from it. */
  'complaints-by-division': byCategory(
    (query) => complaints(scoped(query).own),
    (row) => row.division,
  ),
};
